import { LeftNavLinkProps, LeftNav as TwoUILeftNav, useDialogController } from '@amx/ux-twoui';
import { useHistory } from 'react-router-dom';
import React, { useCallback } from 'react';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { NAVIGATION_LINKS } from '../../shared/constants';
import { isUserInitiatedIPFEnabledInSetting } from '../../InProductFeedback';
import { useAppContext } from '../useAppContext';
import { settingsManager } from '../../shared/settings';
import { AppKeys, getUrlForApp } from '@microsoft/portal-app/lib/environment';
import { useGetUser } from '../../hooks/useGetUser';
import { useGetUserPhoto } from '../../hooks/useGetUserPhoto';
import { createUrl } from '../../shared/utils';
import { useCurrentOrganization } from '../../Account/Organizations/NativeIdentity/useCurrentOrganization';
import { useAuth } from '@iamexperiences/feature-auth';
import { getTelemetry } from '../../Telemetry/Telemetry';
import { useGetProfilePhotoAllowedOperations } from '../../hooks/useUpdateProfilePhoto';
import { EditPhotoDialog } from '../../shared/EditProfilePhoto/EditPhotoDialog';

export interface ILeftNavProps {
  selectedNavLinkKey: string;
}
export const LeftNav: React.FC<ILeftNavProps> = ({ selectedNavLinkKey }) => {
  const history = useHistory();
  const dialogController = useDialogController();
  const { onShowIPF } = useAppContext().InProductFeedbackOption;
  const userInfoQuery = useGetUser();
  const userPhotoQuery = useGetUserPhoto();
  const { showMyAccess } = useCurrentOrganization();
  const allowedOperationsQuery = useGetProfilePhotoAllowedOperations();
  const { auth } = useAuth();

  const [t] = useLocalization(['Account']);
  const isInProductFeedbackEnabled = isUserInitiatedIPFEnabledInSetting(auth.user?.tenantId);
  const securityUrl = getUrlForApp(location, AppKeys.signins);
  securityUrl.pathname = 'security-info';
  if (auth.user?.tenantId) {
    securityUrl.searchParams.set('tenant', auth.user.tenantId);
  }

  const allLeftNavItems: { item: LeftNavLinkProps; show?: boolean }[] = [
    {
      item: {
        text: t('Overview'),
        'aria-label': t('Overview'),
        'data-bi-id': '',
        icon: 'Contact',
        itemKey: NAVIGATION_LINKS.overview.key,
        linkInteraction: {
          interactionType: 'withOnClick',
          onClick: () => history.push(`${NAVIGATION_LINKS.overview.link}`),
        },
        linkType: 'directlink',
      },
      show: true,
    },
    {
      item: {
        text: t('securityInfo'),
        'aria-label': t('securityInfo'),
        'data-bi-id': '',
        icon: 'EditContact',
        itemKey: 'securityInfo',
        linkInteraction: {
          interactionType: 'withHref',
          href: securityUrl.toString(),
        },
        linkType: 'directlink',
      },
      show: settingsManager.get('isMySignInsEnabled'),
    },
    {
      item: {
        text: t('Devices'),
        'aria-label': t('Devices'),
        'data-bi-id': '',
        icon: 'System',
        itemKey: NAVIGATION_LINKS.devices.key,
        linkInteraction: {
          interactionType: 'withOnClick',
          onClick: () => history.push(`${NAVIGATION_LINKS.devices.link}`),
        },
        linkType: 'directlink',
      },
      show: true,
    },
    {
      item: {
        text: t('changePassword'),
        'aria-label': t('changePassword'),
        'data-bi-id': '',
        icon: 'Permissions',
        itemKey: 'password',
        linkInteraction: {
          interactionType: 'withHref',
          href: settingsManager.get('changePasswordFwdLink'),
        },
        linkType: 'directlink',
      },
      show: true,
    },
    {
      item: {
        text: t('Organizations'),
        'aria-label': t('Organizations'),
        'data-bi-id': '',
        icon: 'Work',
        itemKey: NAVIGATION_LINKS.organizations.key,
        linkInteraction: {
          interactionType: 'withOnClick',
          onClick: () => history.push(NAVIGATION_LINKS.organizations.link),
        },
        linkType: 'directlink',
      },
      show: true,
    },
    {
      item: {
        text: t('settingsAndPrivacyCardTitle'),
        'aria-label': t('settingsAndPrivacyCardTitle'),
        'data-bi-id': '',
        icon: 'Settings',
        itemKey: NAVIGATION_LINKS.settingsAndPrivacy.key,
        linkInteraction: {
          interactionType: 'withOnClick',
          onClick: () => history.push(NAVIGATION_LINKS.settingsAndPrivacy.link),
        },
        linkType: 'directlink',
      },
      show: true,
    },
    {
      item: {
        text: t('mySignIns'),
        'aria-label': t('mySignIns'),
        'data-bi-id': '',
        icon: 'Signin',
        itemKey: 'mySignIns',
        linkInteraction: {
          interactionType: 'withHref',
          href: createUrl(AppKeys.signins, false, false, '', auth.user?.tenantId, userInfoQuery.data?.userName),
        },
        linkType: 'directlink',
      },
      show: settingsManager.get('isMySignInsEnabled'),
    },
  ];

  const myStarOnClick = (href: string | undefined, targetApp: string) => {
    const telemetry = getTelemetry();
    telemetry.reportCustomEvent('left-nav/switch-app', {
      currentApp: 'My Account',
      targetApp,
    });

    if (href) {
      const url = new URL(href);
      url.searchParams.set('tenantId', auth.user?.tenantId || '');
      url.searchParams.set('upn', userInfoQuery.data?.userPrincipalName || '');
      window.open(url.toString(), '_blank');
    } else {
      window.open(href, '_blank');
    }
  };

  const myStarApps: LeftNavLinkProps[] = [];
  if (settingsManager.get('myAppsUrl')) {
    myStarApps.push({
      text: t('myApps'),
      'aria-label': t('myApps'),
      'data-bi-id': '',
      icon: 'Apps',
      itemKey: 'myApps',
      linkInteraction: {
        interactionType: 'withOnClick',
        onClick: () => myStarOnClick(settingsManager.get('myAppsUrl'), 'myApps'),
      },
      linkType: 'directlink',
    });
  }
  if (settingsManager.get('isNewMyGroupsEnabled') && settingsManager.get('myGroupsUrl')) {
    myStarApps.push({
      text: t('myGroups'),
      'aria-label': t('myGroups'),
      'data-bi-id': '',
      icon: 'Group',
      itemKey: 'myGroups',
      linkInteraction: {
        interactionType: 'withOnClick',
        onClick: () => myStarOnClick(settingsManager.get('myGroupsUrl'), 'myGroups'),
      },
      linkType: 'directlink',
    });
  }
  if (showMyAccess && settingsManager.get('myAccessUrl')) {
    myStarApps.push({
      text: t('myAccess'),
      'aria-label': t('myAccess'),
      'data-bi-id': '',
      icon: 'ProtectedDocument',
      itemKey: 'myAccess',
      linkInteraction: {
        interactionType: 'withOnClick',
        onClick: () => myStarOnClick(settingsManager.get('myAccessUrl'), 'myAccess'),
      },
      linkType: 'directlink',
    });
  }

  const ipfItem: LeftNavLinkProps = {
    'aria-label': t('inProductFeedback'),
    text: t('inProductFeedback'),
    'data-bi-id': '',
    icon: 'Feedback',
    itemKey: 'feedback',
    linkInteraction: {
      interactionType: 'withOnClick',
      onClick: () => onShowIPF(),
    },
    linkType: 'directlink',
  };

  const onPersonaClick = useCallback(() => {
    dialogController.openDialog(
      <EditPhotoDialog
        displayName={userInfoQuery.data?.displayName || ''}
        photoUrl={userPhotoQuery.data}
        allowedOperations={allowedOperationsQuery.data}
      />,
    );
  }, [allowedOperationsQuery.data, dialogController, userInfoQuery.data?.displayName, userPhotoQuery.data]);

  return (
    <TwoUILeftNav
      bypassBlocksButton={{
        text: t('skipToMainContent'),
        'aria-label': t('skipToMainContent'),
        'data-bi-id': 'leftnav.bypass-blocks-button',
        targetId: 'main-content',
      }}
      expandCollapseNavButton={{
        'aria-label': '',
        'data-bi-id': '',
      }}
      navHeader={{
        headerType: 'profileHeader',
        persona: {
          fallback: 'initials',
          imageUrl: userPhotoQuery.data ?? '',
          itemInteractiveProps:
            settingsManager.get('photoEditorEnabled') && allowedOperationsQuery.data
              ? {
                  'aria-label': t('profile'),
                  'data-bi-id': '',
                  itemInteraction: {
                    interactionType: 'withOnClick',
                    onClick: onPersonaClick,
                  },
                }
              : undefined,
        },
        primaryInfo: {
          text: userInfoQuery.data?.displayName ?? '',
          kind: 'text',
        },
        secondaryInfo: {
          text: userInfoQuery.data?.userPrincipalName ?? '',
        },
      }}
      selectedNavLinkKey={selectedNavLinkKey}
      navLinkGroups={[
        { key: 'account-links', navLinks: allLeftNavItems.filter(({ show }) => show).map(({ item }) => item) },
        {
          key: 'others-apps',
          navLinks: myStarApps,
        },
        ...(isInProductFeedbackEnabled ? [{ key: 'others', navLinks: [ipfItem] }] : []),
      ]}
    />
  );
};
